document.addEventListener('DOMContentLoaded', () => {
  const recCache = new Map();
  let crossSellRendering = false;

  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };

  /* ---- helpers to always get fresh DOM refs ---- */
  const getCrossSell = () => document.querySelector('[data-cross-sell]');
  const getItemsContainer = () => document.querySelector('[data-cross-sell-items]');
  const getTemplate = () => document.querySelector('[data-cross-sell-item]');

  const showCrossSell = (el) => {
    if (!el) return;
    el.style.display = 'block';
    requestAnimationFrame(() => {
      el.classList.add('is-visible');
    });
  };

  const hideCrossSell = (el) => {
    if (!el) return;
    el.classList.remove('is-visible');
    el.style.display = 'none';
  };



  /* ---- cross-sell rendering ---- */
  const MAX_CROSS_SELL = 4;

  const fetchRecommendations = (productId) => {
    if (recCache.has(productId)) {
      return Promise.resolve(recCache.get(productId));
    }
    /* Fetch only related (S&D) intent */
    return fetch(`/recommendations/products.json?product_id=${productId}&limit=${MAX_CROSS_SELL}&intent=related`)
      .then((r) => r.json())
      .then((d) => {
        const products = d.products || [];
        recCache.set(productId, products);
        return products;
      })
      .catch(() => {
        return [];
      });
  };

  const renderCrossSell = () => {
    if (crossSellRendering) return;
    crossSellRendering = true;

    const crossSell = getCrossSell();
    const itemsContainer = getItemsContainer();
    const template = getTemplate();

    if (!crossSell || !itemsContainer || !template) {
      crossSellRendering = false;
      return;
    }

    const cartItems = document.querySelector('cart-drawer-items');
    if (!cartItems || cartItems.classList.contains('is-empty')) {
      hideCrossSell(crossSell);
      crossSellRendering = false;
      return;
    }

    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        /* Re-query DOM again in case section re-rendered during fetch */
        const cs = getCrossSell();
        const ic = getItemsContainer();
        const tmpl = getTemplate();
        if (!cs || !ic || !tmpl) return;



        ic.innerHTML = '';
        hideCrossSell(cs);

        if (!cart.items || cart.items.length === 0) return;

        /* Collect product IDs already in the cart to exclude them */
        const cartProductIds = new Set(cart.items.map((item) => item.product_id));

        /* Fetch recommendations for all unique cart product IDs */
        const uniqueProductIds = [...cartProductIds];
        const fetchPromises = uniqueProductIds.map((pid) => fetchRecommendations(pid));

        Promise.all(fetchPromises)
          .then((results) => {
            /* Merge all recommendations, deduplicate, exclude cart items */
            const seen = new Set();
            const allProducts = [];
            for (const products of results) {
              for (const p of products) {
                if (!seen.has(p.id) && !cartProductIds.has(p.id)) {
                  seen.add(p.id);
                  allProducts.push(p);
                }
              }
            }

            const finalProducts = allProducts.slice(0, MAX_CROSS_SELL);
            if (!finalProducts.length) return;

            /* Re-query DOM one more time after fetches */
            const freshCs = getCrossSell();
            const freshIc = getItemsContainer();
            const freshTmpl = getTemplate();
            if (!freshCs || !freshIc || !freshTmpl) return;
            showCrossSell(freshCs);
            renderProducts(finalProducts, freshIc, freshTmpl);
          })
          .catch(() => {});
      })
      .catch(() => {})
      .finally(() => {
        crossSellRendering = false;
      });
  };

  const renderProducts = (products, container, template) => {
    const currencyCode =
      window.Shopify && window.Shopify.currency && window.Shopify.currency.active
        ? window.Shopify.currency.active
        : 'PHP';
    const locale = (window.Shopify && window.Shopify.locale) || 'en';

    products.forEach((product) => {
      const variant = product.variants && product.variants[0];
      if (!variant) return;

      const clone = template.content.cloneNode(true);
      const card = clone.querySelector('.cart-drawer__cross-sell-card');
      if (card) {
        card.dataset.variantId = variant.id;
        card.href = product.url || '#';
      }

      const img = clone.querySelector('img');
      if (img) {
        img.src = product.featured_image
          ? product.featured_image.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_200x200.$1')
          : '';
        img.alt = product.title || '';
      }

      const titleEl = clone.querySelector('.cart-drawer__cross-sell-title');
      if (titleEl) titleEl.textContent = product.title || '';

      const priceEl = clone.querySelector('.cart-drawer__cross-sell-price');
      if (priceEl) {
        try {
          let currency = currencyCode;
          if (
            variant.presentment_prices &&
            variant.presentment_prices.length > 0 &&
            variant.presentment_prices[0].price
          ) {
            currency = variant.presentment_prices[0].price.currency_code || currencyCode;
          }
          priceEl.textContent = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
          }).format(variant.price / 100);
        } catch (e) {
          priceEl.textContent = (variant.price / 100).toFixed(2);
        }
      }



      container.appendChild(clone);
    });
  };

  /* ---- Free shipping suggestions ---- */
  const MAX_SHIPPING_SUGGESTIONS = 2;

  const fetchShippingSuggestionProducts = async (gapAmount, cartProductIds) => {
    const seen = new Set(cartProductIds);
    const candidates = [];

    const uniquePids = [...cartProductIds];
    const fetchPromises = uniquePids.slice(0, 3).map((pid) =>
      fetch(`/recommendations/products.json?product_id=${pid}&limit=8&intent=related`)
        .then((r) => r.json())
        .then((d) => d.products || [])
        .catch(() => [])
    );

    const recArrays = await Promise.all(fetchPromises);
    for (const products of recArrays) {
      for (const p of products) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        const cheapest = Math.min(...p.variants.map((v) => v.price));
        if (cheapest > 0) candidates.push({ product: p, price: cheapest });
      }
    }

    if (candidates.length < 2) {
      try {
        const res = await fetch('/collections/all/products.json?limit=50&sort_by=price-ascending');
        const data = await res.json();
        for (const p of data.products) {
          if (seen.has(p.id)) continue;
          seen.add(p.id);
          const cheapest = Math.min(...p.variants.map((v) => v.price));
          if (cheapest > 0) candidates.push({ product: p, price: cheapest });
        }
      } catch (e) {}
    }

    candidates.sort((a, b) => Math.abs(a.price - gapAmount) - Math.abs(b.price - gapAmount));

    return candidates.slice(0, MAX_SHIPPING_SUGGESTIONS).map((c) => c.product);
  };

  const renderFreeShippingSuggestions = () => {
    const container = document.querySelector('[data-shipping-gap]');
    if (!container) return;

    const gapAmount = parseInt(container.dataset.shippingGap, 10);
    if (isNaN(gapAmount) || gapAmount <= 0) return;

    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        if (!cart.items || cart.items.length === 0) return;
        const cartProductIds = new Set(cart.items.map((i) => i.product_id));

        fetchShippingSuggestionProducts(gapAmount, cartProductIds).then((products) => {
          container.innerHTML = '';
          if (!products.length) return;

          const currencyCode =
            window.Shopify && window.Shopify.currency && window.Shopify.currency.active
              ? window.Shopify.currency.active
              : 'PHP';
          const locale = (window.Shopify && window.Shopify.locale) || 'en';

          const formatPrice = (price, variant) => {
            try {
              let currency = currencyCode;
              if (
                variant.presentment_prices &&
                variant.presentment_prices.length > 0 &&
                variant.presentment_prices[0].price
              ) {
                currency = variant.presentment_prices[0].price.currency_code || currencyCode;
              }
              return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: currency,
              }).format(price / 100);
            } catch (e) {
              return (price / 100).toFixed(2);
            }
          };

          const productLinks = products
            .map((product) => {
              const variant = product.variants && product.variants[0];
              if (!variant) return null;
              const priceText = formatPrice(variant.price, variant);
              return (
                '<a href="' +
                (product.url || '#') +
                '" class="cart-drawer__free-shipping-suggestion-link">' +
                product.title +
                ' (' +
                priceText +
                ')</a>'
              );
            })
            .filter(Boolean);

          if (!productLinks.length) return;

          let list;
          if (productLinks.length === 1) {
            list = productLinks[0];
          } else {
            const last = productLinks.pop();
            list = productLinks.join(', ') + ' or ' + last;
          }

          const msg = document.createElement('p');
          msg.className = 'cart-drawer__free-shipping-suggestions-text';
          msg.innerHTML = 'Complete your order \u2014 add ' + list + ' to unlock free shipping. Or pay a flat shipping rate at checkout.';
          container.appendChild(msg);
        });
      })
      .catch(() => {});
  };

  const debouncedRenderCrossSell = debounce(renderCrossSell, 300);
  const debouncedRenderShipping = debounce(renderFreeShippingSuggestions, 300);

  /* ---- remove animation on click ---- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.cart-remove-button');
    if (btn) {
      const row = btn.closest('.cart-item');
      if (row) row.classList.add('cart-item--removing');
    }
  });

  /* ---- initial render ---- */
  debouncedRenderCrossSell();
  debouncedRenderShipping();

  /* ---- patch CartDrawer.prototype.renderContents for add-to-cart ---- */
  const patchRenderContents = () => {
    if (typeof CartDrawer === 'undefined') return;
    const origRender = CartDrawer.prototype.renderContents;
    if (!origRender || origRender.__crossSellPatched) return;

    CartDrawer.prototype.renderContents = function (...args) {
      const result = origRender.apply(this, args);
      setTimeout(() => {
        debouncedRenderCrossSell();
        debouncedRenderShipping();
      }, 100);
      return result;
    };
    CartDrawer.prototype.renderContents.__crossSellPatched = true;
  };

  patchRenderContents();

  /* ---- also listen for the pub/sub cartUpdate event ---- */
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      setTimeout(() => {
        debouncedRenderCrossSell();
        debouncedRenderShipping();
      }, 150);
    });
  }
});
